import React from "react";
import { useTranslation } from "react-i18next";
import { translateOptions } from "../../helpers/optionLabels";
import { useTheme } from "@mui/material";
import Typography from "components/basic/typography";
import HouseImage from "resources/images/HouseImage.png";
import { useNavigate } from "react-router-dom";
import AutoComplete from "components/basic/autoComplete";
import { useSnack } from "hooks/useSnack";
import { ChargerEstimatorToolsPagesProps } from "pages/chargerEstimator";
import FormPageLayout from "components/basic/formPageLayout";

const HomeSetup: React.FC<ChargerEstimatorToolsPagesProps> = ({
  chargerEstimatorData,
  setChargerEstimatorData,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("estimator");
  const navigate = useNavigate();
  const showSnack = useSnack();

  const houseTypeOptions = [
    { id: 1, label: "Detached" },
    { id: 2, label: "Semi-Detached" },
    { id: 3, label: "Townhouse" },
  ];
  const locationOptions = [
    { id: 1, label: "Outdoor" },
    { id: 2, label: "Indoor" },
  ];
  const setupOptions = [
    { id: 1, label: "NEMA 14-50 Outlet Only" },
    { id: 2, label: "Tesla Wall Connector with NEMA 14-50" },
    { id: 3, label: "NEMA 14-50 With Level 2 Charger" },
    { id: 4, label: "Tesla Wall hardwired" },
    { id: 5, label: "Hardwired with Level 2 Charger" },
  ];

  const houseType = chargerEstimatorData.houseType
    ? houseTypeOptions.find(
        (opt) => opt.label === chargerEstimatorData.houseType,
      ) || null
    : null;
  const location = chargerEstimatorData.chargerLocation
    ? locationOptions.find(
        (opt) => opt.label === chargerEstimatorData.chargerLocation,
      ) || null
    : null;
  const setup = chargerEstimatorData.chargerSetup
    ? setupOptions.find(
        (opt) => opt.label === chargerEstimatorData.chargerSetup,
      ) || null
    : null;

  const fieldLabelStyle: React.CSSProperties = {
    marginBottom: theme.spacing(1),
  };

  const homeSetupFields = [
    {
      key: "houseType",
      title: t("home.house_type"),
      value: houseType?.id || null,
      options: houseTypeOptions,
      onChange: (value: { id: number; label: string } | null) =>
        setChargerEstimatorData({
          ...chargerEstimatorData,
          houseType: value?.label || "",
        }),
    },
    {
      key: "location",
      title: t("home.location"),
      value: location?.id || null,
      options: locationOptions,
      onChange: (value: { id: number; label: string } | null) =>
        setChargerEstimatorData({
          ...chargerEstimatorData,
          chargerLocation: value?.label || "",
        }),
    },
    {
      key: "setup",
      title: t("home.setup"),
      value: setup?.id || null,
      options: setupOptions,
      onChange: (value: { id: number; label: string } | null) =>
        setChargerEstimatorData({
          ...chargerEstimatorData,
          chargerSetup: value?.label || "",
        }),
    },
  ] as const;

  const handleNext = () => {
    if (!chargerEstimatorData.houseType) {
      showSnack(t("home.house_required"), "error");
      return;
    }
    if (!chargerEstimatorData.chargerLocation) {
      showSnack(t("home.location_required"), "error");
      return;
    }
    if (!chargerEstimatorData.chargerSetup) {
      showSnack(t("home.setup_required"), "error");
      return;
    }
    navigate("?currentPage=powerAccess");
  };

  return (
    <FormPageLayout
      imageSrc={HouseImage}
      imageAlt={t("home.image_alt")}
      imagePosition="left"
      title={
        <Typography
          style={{
            fontWeight: "bold",
            fontSize: "2.5em",
            marginBottom: theme.spacing(3),
          }}
          variant="h2"
          align="inherit"
        >
          {t("home.title")}
        </Typography>
      }
      backAction={{
        label: t("nav.back"),
        color: "primary",
        onClick: () => navigate("?currentPage=vehicleType"),
      }}
      primaryAction={{
        label: t("nav.next"),
        color: "warning",
        onClick: handleNext,
      }}
    >
      {homeSetupFields.map((field) => (
        <div key={field.key}>
          <Typography style={fieldLabelStyle} variant="body2" align="inherit">
            {field.title}
          </Typography>

          <AutoComplete
            name="ev"
            value={field.value}
            options={translateOptions(t, field.options)}
            label={t("nav.please_select")}
            onChange={(value) =>
              field.onChange(
                field.options.find((o) => o.id === value?.id) ?? null,
              )
            }
            size="tiny"
            textAlign="center"
          />
        </div>
      ))}
    </FormPageLayout>
  );
};

export default HomeSetup;
